import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import type { ReportTocGroup, ReportView, ViewBlock } from '@/data/schemas';
import { cm, createDocumentConfig, pdfColors, tableLayouts } from './theme.pdf';
import { SECTION_SPACING } from './pdfMake/layout';
import { blockToContentForm } from './pdfMake/blockRenderers';
import { sectionToContent } from './pdfMake/sectionRenderer';
import { PDF_BACKGROUND_DATA_URL } from './pdfMake/pdfBackground';

function createBackground(): (
  currentPage: number,
  pageSize: { width: number; height: number }
) => Content {
  return (_currentPage: number, pageSize: { width: number; height: number }) => {
    return {
      image: PDF_BACKGROUND_DATA_URL,
      fit: [pageSize.width, pageSize.height],
      absolutePosition: {
        x: 0,
        y: 0,
      },
    };
  };
}

function createChangeLegend(): Content {
  return {
    table: {
      widths: [cm(0.6), '*'],
      body: [
        [
          { text: '', fillColor: pdfColors.yellow },
          {
            text: "Modifiche rispetto all'ultima versione",
            style: 'legendText',
          },
        ],
      ],
    },
    layout: tableLayouts.noBorders,
    margin: [0, 0, 0, SECTION_SPACING / 2],
  };
}

function createFooter(
  margins: [number, number, number, number],
  fileName?: string
): (currentPage: number, pageCount: number) => Content {
  return (currentPage: number, pageCount: number) => ({
    margin: [margins[0], cm(0.2), margins[2], 0],
    columns: [
      {
        text: '',
        style: 'footerText',
        alignment: 'left',
      },
      {
        text: fileName ?? '',
        style: 'footerText',
        alignment: 'center',
      },
      {
        text: `Pagina ${currentPage} di ${pageCount}`,
        style: 'footerText',
        alignment: 'right',
      },
    ],
  });
}

export const generateDocumentDefinition = ({
  view,
  isDraft = false,
  showChanges = false,
  fileName,
}: {
  view: ReportView;
  isDraft?: boolean;
  showChanges?: boolean;
  fileName?: string;
}): TDocumentDefinitions => {
  const baseConfig = createDocumentConfig(view.meta.title, isDraft);
  const fallbackMargins: [number, number, number, number] = [cm(1.5), cm(2.95), cm(1.5), cm(1.3)];

  const margins: [number, number, number, number] = (() => {
    const pageMargins = baseConfig.pageMargins;
    if (pageMargins === undefined) {
      return fallbackMargins;
    }
    if (typeof pageMargins === 'number') {
      return [pageMargins, pageMargins, pageMargins, pageMargins];
    }
    if (pageMargins.length === 2) {
      const [horizontal, vertical] = pageMargins;
      return [horizontal, vertical, horizontal, vertical];
    }
    if (pageMargins.length === 4) {
      return pageMargins as [number, number, number, number];
    }
    return fallbackMargins;
  })();

  const content: Content[] = [];

  if (showChanges) {
    content.push(createChangeLegend());
  }

  const sectionLookup = new Map(view.sections.map((section) => [section.id, section]));
  const orderedGroups = view.toc.slice().sort((a, b) => a.order - b.order);

  const renderFrontespizio = (
    group: ReportTocGroup,
    options: { pageBreakAfter?: boolean } = {}
  ): Content | null => {
    const rows: Content[][] = [];
    const separatorLines = new Set<number>();

    group.items.forEach((item) => {
      if (item.type === 'section') {
        const section = sectionLookup.get(item.sectionId);
        if (!section) return;

        const blocks = section.blocks as ViewBlock[];
        if (rows.length > 0) {
          separatorLines.add(rows.length);
        }
        rows.push([
          {
            text: item.label,
            style: 'formSectionLabel',
            bold: true,
            margin: [0, 0, 0, 0],
          },
          {
            stack: blocks.map((block) => blockToContentForm(block, { flattenRowGroups: true })),
            margin: [0, 0, 0, 0],
          },
        ]);
        return;
      }

      item.entries.forEach((entry) => {
        const section = sectionLookup.get(entry.sectionId);
        if (!section) return;
        const blocks = section.blocks as ViewBlock[];
        if (rows.length > 0) {
          separatorLines.add(rows.length);
        }
        rows.push([
          {
            text: entry.label,
            style: 'formSectionLabel',
            bold: true,
            margin: [0, 0, 0, 0],
          },
          {
            stack: blocks.map((block) => blockToContentForm(block, { flattenRowGroups: true })),
            margin: [0, 0, 0, 0],
          },
        ]);
      });
    });

    if (rows.length === 0) {
      return null;
    }

    const formOuterLayout = tableLayouts.formOuter as Exclude<
      (typeof tableLayouts)['formOuter'],
      string
    >;

    const frontespizioLayout = {
      hLineWidth: (i: number) => (separatorLines.has(i) ? 0.8 : 0.5),
      vLineWidth: formOuterLayout.vLineWidth,
      hLineColor: (i: number) => (separatorLines.has(i) ? pdfColors.black : pdfColors.borderGray),
      vLineColor: formOuterLayout.vLineColor,
      paddingLeft: formOuterLayout.paddingLeft,
      paddingRight: formOuterLayout.paddingRight,
      paddingTop: formOuterLayout.paddingTop,
      paddingBottom: formOuterLayout.paddingBottom,
    };

    const frontTable: Content = {
      unbreakable: true,
      table: {
        widths: [cm(4.2), '*'],
        dontBreakRows: true,
        body: rows,
      },
      layout: frontespizioLayout,
    };

    return {
      stack: [
        {
          text: 'VERBALE DI SOPRALLUOGO VERIFICA GAS TOSSICI',
          style: 'reportTitle',
          alignment: 'center',
          margin: [0, 0, 0, cm(0.25)],
        },
        frontTable,
      ],
      margin: [0, 0, 0, SECTION_SPACING],
      pageBreak: options.pageBreakAfter ? ('after' as const) : undefined,
    };
  };

  orderedGroups.forEach((group, groupIndex) => {
    if (group.id === 'frontespizio') {
      const pageBreakAfter = orderedGroups.length > 1 && groupIndex === 0;
      const front = renderFrontespizio(group, { pageBreakAfter });
      if (front) {
        content.push(front);
      }
      return;
    }

    group.items.forEach((item) => {
      if (item.type === 'section') {
        const section = sectionLookup.get(item.sectionId);
        if (section) {
          content.push(sectionToContent(section));
        }
        return;
      }

      item.entries.forEach((entry) => {
        const section = sectionLookup.get(entry.sectionId);
        if (section) {
          content.push(sectionToContent(section));
        }
      });
    });
  });

  return {
    ...baseConfig,
    content,
    footer: createFooter(margins, fileName),
    background: createBackground(),
    pageMargins: margins,
    styles: {
      ...(baseConfig.styles ?? {}),
      reportTitle: {
        fontSize: 13,
        bold: true,
        color: pdfColors.black,
        alignment: 'center',
      },
      formSectionLabel: {
        fontSize: 8.5,
        bold: true,
        color: pdfColors.black,
      },
      formBoxTitle: {
        fontSize: 8.8,
        bold: true,
        color: pdfColors.black,
      },
      sectionTitle: {
        fontSize: 10,
        bold: true,
        color: pdfColors.black,
        margin: [0, 0, 0, cm(0.2)],
      },
      sectionDescription: {
        fontSize: 9,
        color: pdfColors.darkGray,
        italics: true,
        margin: [0, 0, 0, cm(0.3)],
      },
      sectionCommentLabel: {
        fontSize: 8,
        bold: true,
        color: pdfColors.noteGray,
        margin: [0, 0, 0, cm(0.1)],
      },
      sectionCommentText: {
        fontSize: 8.5,
        color: pdfColors.black,
        margin: [0, 0, 0, cm(0.2)],
      },
      rowGroupLabel: {
        fontSize: 8.5,
        bold: true,
        color: pdfColors.black,
        margin: [0, 0, 0, 0],
      },
      rowGroupDescription: {
        fontSize: 8.2,
        color: pdfColors.darkGray,
        italics: true,
        margin: [0, 0, 0, 0],
      },
      bodyNormal: {
        fontSize: 8.2,
        color: pdfColors.black,
        lineHeight: 1.15,
      },
      bodyStrong: {
        fontSize: 8.2,
        bold: true,
        color: pdfColors.black,
      },
      bodyMuted: {
        fontSize: 8.2,
        color: pdfColors.darkGray,
        italics: true,
      },
      legendText: {
        fontSize: 8,
        color: pdfColors.darkGray,
      },
      footerText: {
        fontSize: 7,
        color: pdfColors.darkGray,
      },
    },
    defaultStyle: {
      ...(baseConfig.defaultStyle ?? {}),
      font: 'Roboto',
      fontSize: 8.5,
      color: pdfColors.black,
    },
  };
};
